import { useState } from "react";

import clsx from "clsx";
import {
  BooleanParam,
  StringParam,
  useQueryParams,
  withDefault,
} from "use-query-params";

import { Steps } from "@pages/onboarding/steps";
import { PrimaryButton } from "@shared/buttons";
import { Modal } from "@shared/modal";

import { StepCompletionConfirmation as WrappedStepCompletionConfirmation } from "../step-completion-confirmation";

import { type OrdersApiStepMetadata } from "./step-metadata";

export function getMissingSteps(
  stepMetadata: OrdersApiStepMetadata,
  isShopifyConnected: boolean,
): [string, string][] {
  const missingSteps: [string, string][] = [];
  if (stepMetadata?.shopifyOrCustom === "shopify") {
    if (!isShopifyConnected) {
      missingSteps.push([
        "a",
        "Please download the Northbeam app to your Shopify store",
      ]);
    }
    if (stepMetadata?.doNotUseOrderTags) {
      return missingSteps;
    }
    if (!stepMetadata?.apiOrderTagsConfirmed) {
      missingSteps.push(["c", "Add order tags in Shopify"]);
    }
    if (!stepMetadata?.orderExclusionsComplete) {
      missingSteps.push([
        "d",
        "Configure Northbeam order exclusions (optional)",
      ]);
    }
  } else {
    if (!stepMetadata?.isApiTestingComplete) {
      missingSteps.push([
        "a",
        "Add order tracking to Northbeam using the ‘Orders API’",
      ]);
    }
    if (
      !stepMetadata?.doNotUseOrderTags &&
      !stepMetadata?.apiOrderTagsConfirmed
    ) {
      missingSteps.push([
        "c",
        "Configure Order Tagging through the ‘Northbeam Orders API’",
      ]);
    }
    if (
      !stepMetadata?.doNotUseOrderTags &&
      !stepMetadata?.orderExclusionsComplete
    ) {
      missingSteps.push([
        "d",
        "Configure Northbeam order exclusions (optional)",
      ]);
    }
  }

  return missingSteps;
}

export const MissingStepsModal = ({
  isOpen,
  onRequestClose,
  missingSteps,
  footer,
}: {
  isOpen: boolean;
  onRequestClose: () => void;
  missingSteps: [string, string][];
  footer: React.ReactNode;
}) => {
  const [_, setSearchParams] = useQueryParams({
    subStep: withDefault(StringParam, ""),
    noScroll: withDefault(BooleanParam, false),
  });
  return (
    <Modal
      isOpen={isOpen}
      titleContent={
        <div className="font-bold">
          Complete following steps to mark this step ‘complete’
        </div>
      }
      onClose={onRequestClose}
    >
      <div className="h-[320px] overflow-y-auto">
        {missingSteps.map(([step, title], idx) => (
          <div
            key={step}
            className={clsx(
              "w-full py-5 flex items-center justify-between border-b border-[#D9D9D9]",
              !idx && "border-t",
            )}
          >
            <span className="text-md font-semibold leading-7">
              8{step}. {title}
            </span>
            <div className="w-32 flex flex-col gap-0.5 rounded-3xl px-5 py-1.5 bg-interstellar-3 justify-start items-center">
              <span className="text-[10px] font-semibold uppercase leading-tight tracking-wide">
                Incomplete
              </span>
              <a
                href="/"
                onClick={(e) => {
                  e.preventDefault();
                  onRequestClose();
                  setSearchParams({ subStep: step, noScroll: undefined });
                }}
                className="text-base leading-none font-semibold text-md no-underline"
              >
                Go to step
              </a>
            </div>
          </div>
        ))}
      </div>
      <div className="w-full h-10 self-stretch justify-end items-end inline-flex mb-5">
        {footer}
      </div>
    </Modal>
  );
};

export const StepCompletionConfirmation = ({
  stepMetadata,
  usesShopify,
  nextStep,
  toggleStepComplete,
}: {
  stepMetadata: OrdersApiStepMetadata;
  usesShopify?: boolean;
  nextStep: (() => void) | undefined;
  toggleStepComplete: () => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const missingSteps = getMissingSteps(stepMetadata, !!usesShopify);

  return (
    <>
      <MissingStepsModal
        isOpen={isOpen}
        onRequestClose={() => {
          setIsOpen(false);
        }}
        missingSteps={missingSteps}
        footer={
          <PrimaryButton
            className="w-[120px] h-full font-semibold uppercase rounded-[20px]"
            onClick={() => {
              setIsOpen(false);
            }}
          >
            OK
          </PrimaryButton>
        }
      />
      <WrappedStepCompletionConfirmation
        labelSuffix="all the steps for adding orders"
        step={Steps.ORDERS_API}
        nextStep={nextStep}
        toggleStepComplete={toggleStepComplete}
        onSelect={() => {
          setIsOpen(missingSteps.length > 0);

          return missingSteps.length === 0;
        }}
      />
    </>
  );
};
